// components/ImageCropModal.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { X, RotateCcw, Check, Pencil } from '@/components/ui/lucide-memo';
import ReferenceAnnotator from './ReferenceAnnotator';
import anime from 'animejs';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import {
  createCenterSquarePolygon,
  getPolygonBounds,
  normalizePolygonClockwise,
  renderKeepAreaPreview,
} from '#lib/layer-cut-utils';

const translations = {
  en: {
    doubleClickHint: 'Double-click to finish here',
    altPeekHint: 'Hold Alt to preview what is behind',
  },
  fr: {
    doubleClickHint: 'Double-cliquez pour terminer ici',
    altPeekHint: 'Maintenez Alt pour voir ce qu\'il y a derriere',
  },
};

const SNAP_THRESHOLD = 32; // in image pixels

const HIDDEN_SOURCE_IMAGE_STYLE: React.CSSProperties = { display: 'none' };
const PREVIEW_IMAGE_STYLE: React.CSSProperties = {
  opacity: 1,
  visibility: 'visible',
  willChange: 'opacity, visibility',
};
const BEHIND_IMAGE_STYLE: React.CSSProperties = {
  opacity: 0,
  visibility: 'hidden',
  willChange: 'opacity, visibility',
};

export type CropResult = {
  polygons: { x: number; y: number }[][];
  file?: File;
};

interface ImageCropModalProps {
  isOpen: boolean;
  imageFile: File | null;
  alternateImageFile?: File | null;
  initialPolygon: { x: number; y: number }[][] | null;
  imageIndex: number | null;
  onClose: () => void;
  onConfirm?: (result: CropResult, originalIndex: number) => void;
  sourceRect?: DOMRect | null;
  lang?: 'en' | 'fr';
  instruction?: string;
  outputMode?: 'generateFile' | 'pathOnly';
  forceOutputSize?: { width: number; height: number };
  forceOutputFormat?: 'image/webp' | 'image/png';
  autoConfirmOnPreview?: boolean;
}

const ImageCropModal: React.FC<ImageCropModalProps> = ({ 
  isOpen, 
  imageFile, 
  alternateImageFile,
  initialPolygon, 
  imageIndex, 
  onClose, 
  onConfirm, 
  sourceRect, 
  lang, 
  instruction, 
  outputMode = 'generateFile',
  forceOutputSize,
  forceOutputFormat,
  autoConfirmOnPreview = true
}) => {
  const { kernel } = useKernel();
  const storeLang = useKernelStore((state) => state.language);
  const resolvedLang = lang ?? storeLang;
  const t = translations[resolvedLang];
  const sourceImageRef = useRef<HTMLImageElement>(null);
  const displayImageRef = useRef<HTMLImageElement>(null);
  const alternateDisplayImageRef = useRef<HTMLImageElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const modalContentRef = useRef<HTMLDivElement>(null);
  const currentPolygonRef = useRef<{ x: number; y: number }[]>([]);
  const isAltKeyPressed = useKernelStore((state) => state.isAltKeyPressed);

  const [polygons, setPolygons] = useState<({ x: number; y: number }[])[]>([]);
  const [currentPolygon, setCurrentPolygon] = useState<{ x: number; y: number }[]>([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [croppedImageUrl, setCroppedImageUrl] = useState<string | null>(null);
  const [drawingBackgroundImageUrl, setDrawingBackgroundImageUrl] = useState<string | null>(null);
  const [imageSize, setImageSize] = useState<{ width: number; height: number } | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [alternateImageUrl, setAlternateImageUrl] = useState<string | null>(null);
  const [mousePos, setMousePos] = useState<{ x: number; y: number } | null>(null);
  const [isSnapping, setIsSnapping] = useState(false);
  const [showDoubleClickHint, setShowDoubleClickHint] = useState(false);
  // 'crop' = the polygon-lasso crop tool (default); 'annotate' = freehand markup
  // so the user can scribble/circle on the reference to guide the agent.
  const [mode, setMode] = useState<'crop' | 'annotate'>('crop');
  const mouseMoveTimer = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    currentPolygonRef.current = currentPolygon;
  }, [currentPolygon]);

  useEffect(() => {
    if (imageFile) {
      const url = URL.createObjectURL(imageFile);
      setImageUrl(url);
      if (initialPolygon) {
        setPolygons(initialPolygon);
      } else {
        setPolygons([]);
      }
      setCurrentPolygon([]);
      setDrawingBackgroundImageUrl(null);
      setMode('crop'); // always start on the crop tool for a fresh image
      return () => {
        URL.revokeObjectURL(url);
      };
    }
    setImageUrl(null);
    setPolygons([]);
    setCurrentPolygon([]);
    setDrawingBackgroundImageUrl(null);
  }, [imageFile, initialPolygon]);

  useEffect(() => {
    if (!alternateImageFile) {
      setAlternateImageUrl(null);
      return;
    }
    const url = URL.createObjectURL(alternateImageFile);
    setAlternateImageUrl(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [alternateImageFile]);

  useEffect(() => {
    if (!isOpen) {
      const baseEl = displayImageRef.current;
      const behindEl = alternateDisplayImageRef.current;
      if (baseEl) {
        baseEl.style.visibility = 'visible';
        baseEl.style.opacity = '1';
      }
      if (behindEl) {
        behindEl.style.visibility = 'hidden';
        behindEl.style.opacity = '0';
      }
    }
  }, [isOpen]);

  // Animation effects
  useEffect(() => {
    const imageFrame = modalContentRef.current;
    const imageToAnimate = displayImageRef.current;

    if (imageToAnimate) {
      imageToAnimate.style.visibility = 'hidden';
    }

    if (!imageFrame || !imageToAnimate || !imageSize){
      return;
    } 

    const frameRect = imageFrame.getBoundingClientRect();
    const imgAspectRatio = imageSize.width / imageSize.height;
    const frameAspectRatio = frameRect.width / frameRect.height;

    let finalImgW, finalImgH;
    if (imgAspectRatio > frameAspectRatio) {
      finalImgW = frameRect.width;
      finalImgH = frameRect.width / imgAspectRatio;
    } else {
      finalImgH = frameRect.height;
      finalImgW = frameRect.height * imgAspectRatio;
    }
    const finalImgTop = frameRect.top + (frameRect.height - finalImgH) / 2;
    const finalImgLeft = frameRect.left + (frameRect.width - finalImgW) / 2;

    const initialScaleX = sourceRect ? sourceRect.width / finalImgW : 0;
    const initialScaleY = sourceRect ? sourceRect.height / finalImgH : 0;
    const initialTranslateX = sourceRect ? (sourceRect.left + sourceRect.width / 2) - (finalImgLeft + finalImgW / 2) : 0;
    const initialTranslateY = sourceRect ? (sourceRect.top + sourceRect.height / 2) - (finalImgTop + finalImgH / 2) : 0;

    anime({
      targets: imageToAnimate,
      translateX: [initialTranslateX, 0],
      translateY: [initialTranslateY, 0],
      scaleX: [initialScaleX, 1],
      scaleY: [initialScaleY, 1],
      duration: 300,
      easing: 'linear',
      begin: () => {
        if (displayImageRef.current) displayImageRef.current.style.visibility = 'visible';
        if (kernel.current?.getSpectacleMode()) {
          kernel.current?.playUISound('QUICK_SWITCH', { volume: 0.3 });
        }
      },
    });

    return () => {
      anime.remove(imageToAnimate);
    };
  }, [isOpen, imageSize, sourceRect, kernel]);

  const hideHintAndClearTimer = useCallback(() => {
    if (mouseMoveTimer.current) clearTimeout(mouseMoveTimer.current);
    setShowDoubleClickHint(false);
  }, []);

  const setAltPeekVisibility = useCallback((showBehind: boolean) => {
    const baseEl = displayImageRef.current;
    const behindEl = alternateDisplayImageRef.current;
    if (!baseEl) return;
    if (!behindEl) {
      baseEl.style.visibility = 'visible';
      baseEl.style.opacity = '1';
      return;
    }

    if (showBehind) {
      baseEl.style.visibility = 'hidden';
      baseEl.style.opacity = '0';
      behindEl.style.visibility = 'visible';
      behindEl.style.opacity = '1';
    } else {
      baseEl.style.visibility = 'visible';
      baseEl.style.opacity = '1';
      behindEl.style.visibility = 'hidden';
      behindEl.style.opacity = '0';
    }
  }, []);

  const handleCancel = useCallback(() => {
    hideHintAndClearTimer();
    const imageFrame = modalContentRef.current;
    const imageToAnimate = displayImageRef.current;
    if (!imageFrame || !imageToAnimate || !imageSize) {
      onClose();
      return;
    }

    const currentImgRect = imageToAnimate.getBoundingClientRect();
    const targetScaleX = sourceRect ? sourceRect.width / currentImgRect.width : 0;
    const targetScaleY = sourceRect ? sourceRect.height / currentImgRect.height : 0;
    const targetTranslateX = sourceRect ? (sourceRect.left + sourceRect.width / 2) - (currentImgRect.left + currentImgRect.width / 2) : 0;
    const targetTranslateY = sourceRect ? (sourceRect.top + sourceRect.height / 2) - (currentImgRect.top + currentImgRect.height / 2) : 0;

    anime({
      targets: imageToAnimate,
      translateX: targetTranslateX,
      translateY: targetTranslateY,
      scaleX: targetScaleX,
      scaleY: targetScaleY,
      duration: 200,
      easing: 'easeInQuad',
      complete: () => {
        setPolygons([]);
        setCurrentPolygon([]);
        setIsDrawing(false);
        setCroppedImageUrl(null);
        setDrawingBackgroundImageUrl(null);
        setImageSize(null);
        onClose();
      }
    });
  }, [onClose, sourceRect, imageSize, hideHintAndClearTimer]);

  const generateDrawingBackground = useCallback((polysToDraw: { x: number; y: number }[][]): string | null => {
    if (!sourceImageRef.current || !imageSize) {
      return null;
    }
    const canvas = document.createElement('canvas');
    canvas.width = imageSize.width;
    canvas.height = imageSize.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    renderKeepAreaPreview(ctx, sourceImageRef.current, imageSize.width, imageSize.height, polysToDraw, 0.5);

    return canvas.toDataURL();
  }, [imageSize]);

  const generateFinalCroppedFile = useCallback(async (polysToCrop: { x: number; y: number }[][]): Promise<File | null> => {
    if (polysToCrop.length === 0 || !sourceImageRef.current || !imageSize) {
      return null;
    }

    const bounds = getPolygonBounds(polysToCrop);
    if (!bounds) return null;
    const { minX, minY, maxX, maxY } = bounds;

    let cropX = minX;
    let cropY = minY;
    let cropWidth = maxX - minX;
    let cropHeight = maxY - minY;

    if (cropWidth <= 0 || cropHeight <= 0) return null;

    // --- AVATAR LOGIC START ---
    if (forceOutputSize) {
      const centerX = cropX + cropWidth / 2;
      const centerY = cropY + cropHeight / 2;
      
      // Determine the side length of the square crop
      let sideLength = Math.max(cropWidth, cropHeight);
      
      // Clamp sideLength to image dimensions to prevent void
      if (sideLength > imageSize.width) sideLength = imageSize.width;
      if (sideLength > imageSize.height) sideLength = imageSize.height;

      // Recalculate crop area to be a square centered on the selection
      let newX = centerX - sideLength / 2;
      let newY = centerY - sideLength / 2;

      // Shift to fit within image bounds
      if (newX < 0) newX = 0;
      if (newY < 0) newY = 0;
      if (newX + sideLength > imageSize.width) newX = imageSize.width - sideLength;
      if (newY + sideLength > imageSize.height) newY = imageSize.height - sideLength;

      cropX = newX;
      cropY = newY;
      cropWidth = sideLength;
      cropHeight = sideLength;
    }
    // --- AVATAR LOGIC END ---

    const finalCanvas = document.createElement('canvas');
    finalCanvas.width = forceOutputSize ? forceOutputSize.width : cropWidth;
    finalCanvas.height = forceOutputSize ? forceOutputSize.height : cropHeight;
    
    const ctx = finalCanvas.getContext('2d');
    if (!ctx) return null;

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    if (forceOutputSize) {
      ctx.drawImage(
        sourceImageRef.current,
        cropX, cropY, cropWidth, cropHeight,
        0, 0, finalCanvas.width, finalCanvas.height
      );
    } else {
      ctx.translate(-minX, -minY);
      ctx.beginPath();
      for (const poly of polysToCrop) {
        const points = normalizePolygonClockwise(poly);
        const firstPoint = points[0];
        if (!firstPoint) continue;
        ctx.moveTo(firstPoint.x, firstPoint.y);
        for (let i = 1; i < points.length; i++) {
            const point = points[i];
            if (point) ctx.lineTo(point.x, point.y);
        }
        ctx.closePath();
      }
      ctx.clip();
      ctx.drawImage(sourceImageRef.current, 0, 0, imageSize.width, imageSize.height);
    }

    const mimeType = forceOutputFormat || 'image/png';
    return new Promise((resolve) => {
      finalCanvas.toBlob((blob) => {
        if (!blob) {
          resolve(null);
          return;
        }
        const ext = mimeType === 'image/webp' ? 'webp' : 'png';
        const filename = `cropped_${sourceImageRef.current?.src.split('/').pop() || 'image'}.${ext}`;
        const file = new File([blob], filename, { type: mimeType });
        resolve(file);
      }, mimeType, 0.9);
    });
  }, [imageSize, forceOutputSize, forceOutputFormat]);

  const handleConfirmLogic = useCallback(async (polys: { x: number; y: number }[][]) => {
    if (imageIndex === null || !onConfirm) {
      return;
    }

    if (outputMode === 'pathOnly') {
      const result: CropResult = { polygons: polys };
      onConfirm(result, imageIndex);

      if (polys.length > 0 && kernel.current?.getSpectacleMode()) {
        kernel.current?.playUISound('CUT', { volume: 0.3 });
      }
      return;
    }

    if (polys.length > 0) {
      const newFile = await generateFinalCroppedFile(polys);
      if (newFile) {
        const result: CropResult = { polygons: polys, file: newFile };
        onConfirm(result, imageIndex);
        if (kernel.current?.getSpectacleMode()) {
          kernel.current?.playUISound('CUT', { volume: 0.3 });
        }
      }
    } else if (imageFile) {
      const result: CropResult = { polygons: [], file: imageFile };
      onConfirm(result, imageIndex);
    }
  }, [imageIndex, onConfirm, outputMode, generateFinalCroppedFile, imageFile, kernel]);

  const handleAutoConfirm = useCallback(async (polys: { x: number; y: number }[][]) => {
    await handleConfirmLogic(polys);
  }, [handleConfirmLogic]);

  // Annotate mode result: the marked-up image comes back as a ready File. Route it
  // through the SAME onConfirm the crop path uses (empty polygons = no crop), so the
  // caller replaces the reference with the annotated image and closes the editor.
  const confirmWithFile = useCallback((file: File) => {
    if (imageIndex === null || !onConfirm) return;
    onConfirm({ polygons: [], file }, imageIndex);
    handleCancel();
  }, [imageIndex, onConfirm, handleCancel]);

  // NEW: Handle "Quick Validate"
  const handleQuickValidate = useCallback(async () => {
    if (!imageSize) return;

    // If user has drawn something, use it
    if (polygons.length > 0 || currentPolygon.length > 0) {
        if (croppedImageUrl) {
            await handleConfirmLogic(polygons);
            handleCancel();
        } else if (currentPolygon.length > 2) {
            const poly = [...currentPolygon];
            await handleConfirmLogic([...polygons, poly]);
            handleCancel();
        } else if (polygons.length > 0) {
            await handleConfirmLogic(polygons);
            handleCancel();
        }
        return;
    }

    // If NOTHING drawn, do a center crop
    await handleConfirmLogic([createCenterSquarePolygon(imageSize.width, imageSize.height)]);
    handleCancel();

  }, [imageSize, polygons, currentPolygon, croppedImageUrl, handleConfirmLogic, handleCancel]);

  const handleReset = useCallback(() => {
    hideHintAndClearTimer();
    setPolygons([]);
    setCurrentPolygon([]);
    setCroppedImageUrl(null);
    setDrawingBackgroundImageUrl(null);
    setIsDrawing(false);
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx?.clearRect(0, 0, canvas.width, canvas.height);
    }
    if (autoConfirmOnPreview) {
      handleAutoConfirm([]);
    }
  }, [handleAutoConfirm, hideHintAndClearTimer, autoConfirmOnPreview]);

  const getImageMouseCoordinates = (event: React.MouseEvent<HTMLDivElement>) => {
    const displayImage = displayImageRef.current;
    const sourceImage = sourceImageRef.current;
    if (!displayImage || !sourceImage) return { x: 0, y: 0 };

    const rect = displayImage.getBoundingClientRect();
    
    const imageAspectRatio = sourceImage.naturalWidth / sourceImage.naturalHeight;
    const containerAspectRatio = rect.width / rect.height;

    let renderW = rect.width;
    let renderH = rect.height;
    let offsetX = 0;
    let offsetY = 0;

    if (imageAspectRatio > containerAspectRatio) {
      renderH = rect.width / imageAspectRatio;
      offsetY = (rect.height - renderH) / 2;
    } else {
      renderW = rect.height * imageAspectRatio;
      offsetX = (rect.width - renderW) / 2;
    }

    const mouseX = event.clientX - rect.left - offsetX;
    const mouseY = event.clientY - rect.top - offsetY;

    const naturalX = (mouseX / renderW) * sourceImage.naturalWidth;
    const naturalY = (mouseY / renderH) * sourceImage.naturalHeight;

    return { x: naturalX, y: naturalY };
  };

  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    hideHintAndClearTimer();
    if (event.button !== 0) return;

    if (!isDrawing && isSnapping && currentPolygon.length > 2) {
      const firstPoint = currentPolygon[0];
      if (firstPoint) {
        const snappedPolygon = [...currentPolygon, firstPoint];
        handleCropForPreview(snappedPolygon);
        return;
      }
    }

    if (croppedImageUrl) {
      const newBg = generateDrawingBackground(polygons);
      setDrawingBackgroundImageUrl(newBg);
      setCroppedImageUrl(null);
      setCurrentPolygon([]);
    }

    setIsDrawing(true);
    const coords = getImageMouseCoordinates(event);
    setCurrentPolygon(prev => [...prev, coords]);
  };

  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    hideHintAndClearTimer();

    if (currentPolygon.length > 2 && !isDrawing && !croppedImageUrl) {
      mouseMoveTimer.current = setTimeout(() => {
        setShowDoubleClickHint(true);
      }, 500);
    }
    const coords = getImageMouseCoordinates(event);
    setMousePos(coords);

    const len = currentPolygon.length;
    if (len > 2 && !croppedImageUrl) {
      const firstPoint = currentPolygon[0];
      if (firstPoint) {
        const dx = coords.x - firstPoint.x;
        const dy = coords.y - firstPoint.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        setIsSnapping(distance < SNAP_THRESHOLD);
      }
    } else {
      setIsSnapping(false);
    }

    if (!isDrawing) return;
    setCurrentPolygon(prev => [...prev, coords]);
  };

  const handleMouseUp = (event: React.MouseEvent<HTMLDivElement>) => {
    hideHintAndClearTimer();
    if (isDrawing && isSnapping) {
      const firstPoint = currentPolygon[0];
      if (firstPoint) {
        const snappedPolygon = [...currentPolygon, firstPoint];
        handleCropForPreview(snappedPolygon);
      }
    }
    setIsDrawing(false);
  };

  const handleDoubleClick = () => {
    hideHintAndClearTimer();
    if (currentPolygon.length > 2) {
      handleCropForPreview();
    }
  };

  const generateFinalPreview = useCallback((polysToPreview: { x: number; y: number }[][]): string | null => {
    if (!sourceImageRef.current || !imageSize) return null;

    const offscreenCanvas = document.createElement('canvas');
    offscreenCanvas.width = imageSize.width;
    offscreenCanvas.height = imageSize.height;
    const ctx = offscreenCanvas.getContext('2d');
    if (!ctx) return null;

    renderKeepAreaPreview(ctx, sourceImageRef.current, imageSize.width, imageSize.height, polysToPreview, 0.7);

    return offscreenCanvas.toDataURL('image/png');
  }, [imageSize]);

  const handleCropForPreview = useCallback((polygonOverride?: { x: number; y: number }[]) => {
    hideHintAndClearTimer();
    const polygonToFinalize = polygonOverride || currentPolygon;
    if (polygonToFinalize.length < 3) return;

    const allPolygons = [...polygons, polygonToFinalize];
    const url = generateFinalPreview(allPolygons);

    if (url) {
      setCroppedImageUrl(url);
    }
    setPolygons(allPolygons);
    setCurrentPolygon([]);
    setDrawingBackgroundImageUrl(null);
    if (autoConfirmOnPreview) {
      handleAutoConfirm(allPolygons);
    }
  }, [currentPolygon, polygons, handleAutoConfirm, generateFinalPreview, hideHintAndClearTimer, autoConfirmOnPreview]);

  const cancelCurrentPolygon = useCallback(() => {
    hideHintAndClearTimer();
    setCurrentPolygon([]);
    setIsDrawing(false);
    setDrawingBackgroundImageUrl(null);

    if (polygons.length > 0) {
      const url = generateFinalPreview(polygons);
      setCroppedImageUrl(url);
    } else {
      setCroppedImageUrl(null);
    }
  }, [polygons, generateFinalPreview, hideHintAndClearTimer]);

  useEffect(() => {
    if (initialPolygon && initialPolygon.length > 0 && imageSize && sourceImageRef.current) {
      const offscreenCanvas = document.createElement('canvas');
      offscreenCanvas.width = imageSize.width;
      offscreenCanvas.height = imageSize.height;
      const ctx = offscreenCanvas.getContext('2d');
      if (!ctx) return;

      renderKeepAreaPreview(ctx, sourceImageRef.current, imageSize.width, imageSize.height, initialPolygon, 0.7);
      const url = offscreenCanvas.toDataURL('image/png');
      setCroppedImageUrl(url);
    }
  }, [initialPolygon, imageSize]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const image = sourceImageRef.current;
    const displayImage = displayImageRef.current;
    if (!canvas || !image || !imageSize || !displayImage) return;

    if (croppedImageUrl && currentPolygon.length === 0) {
      const ctx = canvas.getContext('2d');
      ctx?.clearRect(0, 0, canvas.width, canvas.height);
      return;
    };

    const canvasRect = canvas.getBoundingClientRect();
    if (canvas.width !== canvasRect.width || canvas.height !== canvasRect.height) {
      canvas.width = canvasRect.width;
      canvas.height = canvasRect.height;
    }
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const imgRect = displayImage.getBoundingClientRect();
    const imageAspectRatio = image.naturalWidth / image.naturalHeight;
    const containerAspectRatio = imgRect.width / imgRect.height;

    let renderW = imgRect.width;
    let renderH = imgRect.height;
    let offsetX = 0;
    let offsetY = 0;

    if (imageAspectRatio > containerAspectRatio) {
      renderH = imgRect.width / imageAspectRatio;
      offsetY = (imgRect.height - renderH) / 2;
    } else {
      renderW = imgRect.height * imageAspectRatio;
      offsetX = (imgRect.width - renderW) / 2;
    }

    const imageToCanvas = (p: {x: number, y: number}) => {
        const canvasX = (imgRect.left - canvasRect.left) + offsetX + (p.x / imageSize.width) * renderW;
        const canvasY = (imgRect.top - canvasRect.top) + offsetY + (p.y / imageSize.height) * renderH;
        return { x: canvasX, y: canvasY };
    }

    ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.lineWidth = 2;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';

    if (currentPolygon.length > 0) {
      ctx.beginPath();
      const firstPolyPoint = currentPolygon[0];
      if (firstPolyPoint) {
        const firstPoint = imageToCanvas(firstPolyPoint);
        ctx.moveTo(firstPoint.x, firstPoint.y);
      }
      for (let i = 1; i < currentPolygon.length; i++) {
        const polyPoint = currentPolygon[i];
        if (polyPoint) {
          const point = imageToCanvas(polyPoint);
          ctx.lineTo(point.x, point.y);
        }
      }
      ctx.stroke();

      const lastPolyPoint = currentPolygon[currentPolygon.length - 1];
      if (lastPolyPoint && mousePos) {
        const from = imageToCanvas(lastPolyPoint);
        const to = isSnapping && currentPolygon[0]
          ? imageToCanvas(currentPolygon[0])
          : imageToCanvas(mousePos);
        const startPoint = currentPolygon[0];

        ctx.save();
        ctx.setLineDash([4, 4]);
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.7)';

        ctx.beginPath();
        ctx.moveTo(from.x, from.y);
        ctx.lineTo(to.x, to.y);
        ctx.stroke();

        if (startPoint) {
          const startCanvasPoint = imageToCanvas(startPoint);
          ctx.beginPath();
          ctx.moveTo(to.x, to.y);
          ctx.lineTo(startCanvasPoint.x, startCanvasPoint.y);
          ctx.stroke();
        }

        ctx.restore();
      }
    }

    if (currentPolygon.length > 0 && !croppedImageUrl) {
      const firstPoint = currentPolygon[0];
      if (firstPoint) {
        const anchorPos = imageToCanvas(firstPoint);
        const radius = isSnapping ? 10 : 5;
        const alpha = isSnapping ? 1.0 : 0.7;

        ctx.strokeStyle = `rgba(0, 0, 0, ${alpha * 0.8})`;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(anchorPos.x, anchorPos.y, radius, 0, 2 * Math.PI);
        ctx.stroke();

        ctx.fillStyle = `rgba(255, 255, 255, ${alpha})`;
        ctx.beginPath();
        ctx.arc(anchorPos.x, anchorPos.y, radius, 0, 2 * Math.PI);
        ctx.fill();
      }
    }
  }, [polygons, currentPolygon, isDrawing, imageSize, croppedImageUrl, mousePos, isSnapping, generateDrawingBackground]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (currentPolygonRef.current.length > 0) {
          cancelCurrentPolygon();
        } else {
          handleCancel();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [isOpen, handleCancel, cancelCurrentPolygon]);

  const handleSourceImageLoad = useCallback((event: React.SyntheticEvent<HTMLImageElement>) => {
    if (!imageSize) {
      const img = event.currentTarget;
      setImageSize({ width: img.naturalWidth, height: img.naturalHeight });
    }
  }, [imageSize]);

  const handlePreventDragStart = useCallback((event: React.DragEvent<HTMLImageElement>) => {
    event.preventDefault();
  }, []);

  const handleStopPropagationMouseDown = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
  }, []);

  const handleStopPropagation = useCallback((event: React.SyntheticEvent<HTMLDivElement>) => {
    event.stopPropagation();
  }, []);

  const handleEnterAnnotateMode = useCallback(() => {
    setMode('annotate');
  }, []);

  const handleExitAnnotateMode = useCallback(() => {
    setMode('crop');
  }, []);

  const overlayStyle = useMemo<React.CSSProperties>(
    () => ({ cursor: isSnapping ? 'pointer' : 'crosshair', userSelect: 'none' }),
    [isSnapping],
  );

  const defaultDisplayImageUrl = croppedImageUrl || drawingBackgroundImageUrl || imageUrl || undefined;

  useEffect(() => {
    if (!isOpen || !alternateImageUrl) {
      setAltPeekVisibility(false);
      return;
    }
    setAltPeekVisibility(isAltKeyPressed);
  }, [isOpen, defaultDisplayImageUrl, alternateImageUrl, isAltKeyPressed, setAltPeekVisibility]);

  useEffect(() => {
    const preDecode = async (src: string | null | undefined) => {
      if (!src) return;
      try {
        const img = new Image();
        img.decoding = 'async';
        img.src = src;
        if (typeof img.decode === 'function') {
          await img.decode();
        }
      } catch {
        // Best-effort predecode.
      }
    };

    void Promise.all([preDecode(defaultDisplayImageUrl), preDecode(alternateImageUrl)]);
  }, [defaultDisplayImageUrl, alternateImageUrl]);

  if (!isOpen || !imageUrl) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#141414]/92 backdrop-blur-sm"
      aria-modal="true"
      role="dialog"
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onDoubleClick={handleDoubleClick}
      style={overlayStyle}
    >
      <img
        ref={sourceImageRef}
        src={imageUrl}
        alt=""
        style={HIDDEN_SOURCE_IMAGE_STYLE}
        onLoad={handleSourceImageLoad}
      />
      
      <>
        <div
          ref={modalContentRef}
          className="relative w-full h-full max-w-[90vw] max-h-[90vh] flex items-center justify-center"
        >
          <img
            ref={displayImageRef}
            src={defaultDisplayImageUrl}
            alt="Image preview"
            className="max-w-full max-h-full object-contain"
            style={PREVIEW_IMAGE_STYLE}
            onDragStart={handlePreventDragStart}
          />
          {alternateImageUrl && (
            <img
              ref={alternateDisplayImageRef}
              src={alternateImageUrl ?? undefined}
              alt="Behind preview"
              className="absolute inset-0 m-auto max-w-full max-h-full object-contain pointer-events-none"
              style={BEHIND_IMAGE_STYLE}
              onDragStart={handlePreventDragStart}
            />
          )}
          {!croppedImageUrl && (
            <canvas
              ref={canvasRef}
              className="absolute top-0 left-0 w-full h-full pointer-events-none"
            />
          )}
        </div>
        <div className="absolute right-4 top-4 flex items-center gap-1 rounded-lg border border-[#2A2A2A] bg-[#222222]/95 p-1 shadow-xl backdrop-blur-sm">
          <button
            onClick={handleQuickValidate}
            onMouseDown={handleStopPropagationMouseDown}
            className="flex h-8 w-8 items-center justify-center rounded-md bg-white text-black transition-opacity hover:opacity-90"
            title="Use full image"
          >
            <Check size={16} />
          </button>
          <button
            onClick={handleEnterAnnotateMode}
            onMouseDown={handleStopPropagationMouseDown}
            className="flex h-8 w-8 items-center justify-center rounded-md bg-white/[0.05] text-white/65 transition-colors hover:bg-white/[0.1] hover:text-white"
            title="Annotate — draw on the reference to guide the agent"
            aria-label="Annotate reference"
          >
            <Pencil size={16} />
          </button>
          {croppedImageUrl && (
            <button
              onClick={handleReset}
              onMouseDown={handleStopPropagationMouseDown}
              className="flex h-8 w-8 items-center justify-center rounded-md bg-white/[0.05] text-white/65 transition-colors hover:bg-white/[0.1] hover:text-white"
              aria-label="Reset crop"
            >
              <RotateCcw size={16} />
            </button>
          )}
          <button
            onClick={handleCancel}
            onMouseDown={handleStopPropagationMouseDown}
            className="flex h-8 w-8 items-center justify-center rounded-md bg-white/[0.05] text-white/65 transition-colors hover:bg-white/[0.1] hover:text-white"
            aria-label="Close image editor"
          >
            <X size={16} />
          </button>
        </div>
        {showDoubleClickHint && (
          <div className="pointer-events-none absolute bottom-4 left-1/2 -translate-x-1/2 animate-in rounded-md border border-[#2A2A2A] bg-[#222222]/95 px-3 py-1.5 text-[12px] text-white/75 shadow-lg backdrop-blur-sm fade-in-50">
            {t.doubleClickHint}
          </div>
        )}
        {alternateImageUrl && (
          <div className="pointer-events-none absolute bottom-4 left-4 rounded-md border border-[#2A2A2A] bg-[#222222]/95 px-3 py-1.5 text-[12px] text-white/75 shadow-lg backdrop-blur-sm">
            {t.altPeekHint}
          </div>
        )}
        {instruction && (
          <div
            className="pointer-events-none absolute left-1/2 top-4 -translate-x-1/2 rounded-md border border-[#2A2A2A] bg-[#222222]/95 px-3 py-1.5 text-center text-[13px] font-medium text-white/80 shadow-lg backdrop-blur-sm"
          >
            {instruction}
          </div>
        )}
        {mode === 'annotate' && imageUrl && (
          // Full overlay so freehand markup runs on its own canvas. Swallow mouse
          // events so the modal's crop-lasso handlers stay dormant while annotating.
          <div
            className="absolute inset-0 z-[60] flex flex-col bg-[#141414]/96 p-4"
            onMouseDown={handleStopPropagation}
            onMouseMove={handleStopPropagation}
            onMouseUp={handleStopPropagation}
            onDoubleClick={handleStopPropagation}
          >
            <ReferenceAnnotator
              imageUrl={imageUrl}
              fileName={imageFile?.name}
              onCancel={handleExitAnnotateMode}
              onConfirm={confirmWithFile}
            />
          </div>
        )}
      </>
    </div>
  );
};
export default React.memo(ImageCropModal);
